import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  ViewStyle,
} from 'react-native';
import { Image } from 'expo-image';
import { Club } from '../types';
import { Colors, Fonts, Radii } from '../constants';
import { imageService, clubColorsService } from '../services';

/** Two-letter initials from a club name ("VV Sportlust" → "VS", "Ajax" → "AJ") */
export const getClubInitials = (name: string): string => {
  const words = name.trim().split(' ').filter(Boolean);
  if (words.length === 0) return '';
  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase();
  }
  return words.slice(0, 2).map(w => w.charAt(0).toUpperCase()).join('');
};

interface ClubMarkProps {
  name: string;
  size?: number;
  style?: ViewStyle;
}

/**
 * Pastel initials circle for clubs without a logo. The colour is hashed from
 * the club name so the same club always gets the same tint.
 */
export const ClubMark: React.FC<ClubMarkProps> = ({ name, size = 28, style }) => (
  <View
    style={[
      styles.mark,
      { width: size, height: size, borderRadius: size / 2, backgroundColor: clubColorsService.getClubColor(name) },
      style,
    ]}
  >
    <Text style={[styles.markText, { fontSize: Math.round(size * 0.4) }]}>{getClubInitials(name)}</Text>
  </View>
);

interface ClubBadgeProps {
  club?: Club | null;
  size?: 'small' | 'medium' | 'large';
  imageOnly?: boolean;
}

export const ClubBadge: React.FC<ClubBadgeProps> = ({
  club,
  size = 'small',
  imageOnly = false
}) => {
  if (!club) return null;

  const logoSize = size === 'large' ? 40 : size === 'medium' ? 30 : 24;
  const containerStyle = size === 'large' ? styles.containerLarge : size === 'medium' ? styles.containerMedium : styles.containerSmall;

  return (
    <View style={[styles.container, containerStyle]}>
      {/* Logo or Initials */}
      {club.logo_url ? (
        <View style={[styles.logoContainer, { width: logoSize, height: logoSize }]}>
          <Image
            source={{ uri: imageService.getLogoUrl(club.logo_url, 'small') }}
            style={styles.logo}
            contentFit="contain"
            transition={200}
            cachePolicy="memory-disk"
          />
        </View>
      ) : (
        <ClubMark name={club.name} size={logoSize} />
      )}

      {/* Club Name */}
      {!imageOnly && (
        <Text
          style={[
            styles.clubName,
            size === 'large' ? styles.clubNameLarge : size === 'medium' ? styles.clubNameMedium : styles.clubNameSmall
          ]}
          numberOfLines={1}
        >
          {club.name}
        </Text>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    maxWidth: '85%', // Allow club names to use available space while maintaining layout aesthetics
    marginBottom: 8,
  },
  containerSmall: {
    gap: 4,
    marginBottom: 4,
  },
  containerMedium: {
    gap: 6,
    marginBottom: 12,
  },
  containerLarge: {
    gap: 8,
    marginBottom: 8,
  },
  logoContainer: {
    borderRadius: Radii.thumb,
    overflow: 'hidden',
    flexShrink: 0,
    backgroundColor: Colors.white,
    borderWidth: 1,
    borderColor: Colors.borderLight,
  },
  logo: {
    width: '100%',
    height: '100%',
  },
  mark: {
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  markText: {
    fontFamily: Fonts.bold,
    color: Colors.textMedium,
  },
  clubName: {
    color: Colors.textMedium,
    fontFamily: Fonts.medium,
    flexShrink: 1,
  },
  clubNameSmall: {
    fontSize: 11,
  },
  clubNameMedium: {
    fontSize: 12,
  },
  clubNameLarge: {
    fontSize: 14,
  },
});
